import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    Date as DateModel,
    SortDirection
} from '@lsq-frontend/remote-services/all-java/common-mk0';
import { BankAccountType } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isEmpty, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { convertDateToLocalDate } from '../../helpers/date-mapping';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    mapBuyerStatusFiltersToPayableLegacyStatusInput,
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus,
    mapPayableStatusToBuyerApprovalStatus
} from '../../helpers/status-mapping';
import { ListInvoiceSummaryForBuyerResponse } from '../../model/list-invoice-summary-for-buyer-response';
import { InvoiceSummary2, PayablesInput, PayablesResponse, TransferType } from '../../model/models';
import { ListInvoiceSummaryForBuyerRequest2 } from './list-invoice-summary-for-buyer';

@Injectable({ providedIn: 'root' })
export class ListInvoiceSummaryOverviewService extends ProtoApiService<
    ListInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerResponse
> {
    public requestType = ListInvoiceSummaryForBuyerRequest2;
    public responseType = ListInvoiceSummaryForBuyerResponse;
    public endpointName = 'ListInvoiceSummaryForBuyer';
    public serviceName = 'financing';

    protected _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListInvoiceSummaryForBuyer';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListInvoiceSummaryForBuyerRequest2
    ): { query: string } | ListInvoiceSummaryForBuyerRequest2 {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection
            } = req.pagination;

            const input: PayablesInput = omitBy(
                {
                    legacyStatuses: mapBuyerStatusFiltersToPayableLegacyStatusInput(
                        req.buyerInvoiceStatus
                    ),
                    companyFilter: {
                        buyerIds: req.refBuyerKeys ? req.refBuyerKeys : [],
                        supplierIds: req.sellerKeys ?? []
                    },
                    payableNameSearch: req.invoiceNumberFilter ?? '',
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] || SortDirection.ASCENDING
                        ),
                        secondarySortColumn: new EnumType(
                            mapBuyerPayableSortColumn(secondarySortColumn)
                        ),
                        secondarySortDirection: new EnumType(
                            SortDirection[secondarySortDirection] || SortDirection.ASCENDING
                        )
                    }
                },
                isEmpty
            );
            const money = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                filteredPayables: {
                    __aliasFor: 'payables',
                    __args: { input },
                    rows: {
                        id: true,
                        name: true,
                        amount: money,
                        originalDueDate: true,
                        dueInDays: true,
                        relationship: {
                            supplier: { id: true, company: { name: true } },
                            buyer: { id: true, company: { name: true } }
                        }
                    },
                    totals: { payableCount: true }
                },
                unfilteredPayables: {
                    __aliasFor: 'payables',
                    __args: { input: { ...input, legacyStatuses: [] } },
                    totals: { payableCount: true }
                }
            });

            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res:
            | GraphQLResponse<{
                  filteredPayables: any;
                  unfilteredPayables: any;
              }>
            | ListInvoiceSummaryForBuyerResponse
    ): ListInvoiceSummaryForBuyerResponse {
        if (this._isPayLaterEnabled) {
            const { filteredPayables, unfilteredPayables } = (res as GraphQLResponse<{
                filteredPayables: any;
                unfilteredPayables: any;
            }>).data;

            return new ListInvoiceSummaryForBuyerResponse({
                ok: true,
                totalCount: filteredPayables.totals.payableCount,
                totalCountBuyerInvoices: unfilteredPayables.totals.payableCount,
                rows: filteredPayables.rows.map((payable) => {
                    const { buyer, supplier } = payable.relationship;
                    const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
                    const invoiceDueDateNew =
                        invoiceDueDate &&
                        new DateModel({
                            day: invoiceDueDate.getDate(),
                            month: invoiceDueDate.getMonth() + 1,
                            year: invoiceDueDate.getFullYear()
                        });

                    return new InvoiceSummary2({
                        invoiceDueDate,
                        invoiceDueDateNew,
                        refInvoiceKey: payable.id, // isGroup ? payable.id : firstApproval.id,
                        invoiceNumber: payable.name, // isGroup ? payable.name : firstApproval.invoice.invoiceNumber,
                        dueIn: payable.dueInDays,
                        sellerName: supplier.company.name,
                        sellerKey: supplier.id,
                        refBuyerKey: buyer.id,
                        finalApprovedAmount: payable.amount
                    });
                })
            });
        }
        return res as ListInvoiceSummaryForBuyerResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
